// FASE 1 PR3/PR4 — bridge utility per migrazione API engine-neutral.
// Dispatcha al path engine-neutral SE l'adapter espone la capability
// `engineNeutralCamera` (Spark prod), altrimenti fallback su API legacy
// (fake adapter nei test orchestrator/tour/measure).
//
// PR3: setCameraDispatch (11 call site setCamera → setCameraState).
// PR4: projectWorldToScreenDispatch + raycastDispatch + resizeDispatch.

import type * as THREE from 'three';
import type { Vec3 } from '../api/types.js';
import { projectWorldToScreen as legacyProjectWorldToScreen } from '../hotspots/projection.js';
import { canRaycast, canUseEngineNeutralCamera, type ViewerAdapter } from './adapter.js';

export interface SetCameraDispatchInput {
  readonly pos: Vec3;
  readonly lookAt: Vec3;
  readonly fov?: number;
}

export function setCameraDispatch(
  adapter: ViewerAdapter,
  input: SetCameraDispatchInput,
): void {
  if (canUseEngineNeutralCamera(adapter)) {
    // setCameraState richiede fov obbligatorio. Read current fov se omesso
    // (ride mid-tick / reduced-motion snap).
    const fov = input.fov ?? adapter.getCameraState().fov;
    adapter.setCameraState({ pos: input.pos, lookAt: input.lookAt, fov });
  } else {
    // Legacy path: fake adapter nei test usano questo. setCamera accetta
    // fov?: number → passare undefined preserva semantica.
    adapter.setCamera(input.pos, input.lookAt, input.fov);
  }
}

// PR4 — projection helper. Codex INDIPENDENT: normalizza a {x, y, visible,
// distance} per back-compat con hotspots/projection.ts:Projected (i 3 call
// site usano .x/.y, NON .screenX/.screenY). Engine-neutral viewer/types
// .Projected {screenX/screenY} viene rimappato qui.
//
// Viewport obbligatorio nel signature (i call site lo costruiscono comunque
// da canvas clientWidth/Height) — engine-neutral path inietta pixelRatio
// corrente; legacy path lo usa direttamente.

export interface BridgeProjection {
  readonly x: number;       // CSS px from canvas top-left
  readonly y: number;       // CSS px from canvas top-left
  readonly visible: boolean;
  readonly distance: number;
}

export interface BridgeViewport {
  readonly width: number;
  readonly height: number;
}

export function projectWorldToScreenDispatch(
  adapter: ViewerAdapter,
  world: Vec3,
  viewport: BridgeViewport,
): BridgeProjection {
  if (canUseEngineNeutralCamera(adapter)) {
    // Engine-neutral: pixelRatio non rilevante per projection (usa width/height
    // CSS), ma signature ViewportSize lo richiede. Leggi corrente.
    const pixelRatio = adapter.getViewport().pixelRatio;
    const proj = adapter.projectWorldToScreen(world, {
      width: viewport.width,
      height: viewport.height,
      pixelRatio,
    });
    return {
      x: proj.screenX,
      y: proj.screenY,
      visible: proj.visible,
      distance: proj.distance,
    };
  }
  // Legacy path: fake adapter ha getCamera() (vedi test/orchestrator.test.ts
  // makeFakeAdapter). Reusa la funzione di hotspots/projection.ts.
  const camera = adapter.getCamera();
  return legacyProjectWorldToScreen(world, camera, viewport);
}

// PR4 — raycast helper. Codex INDIPENDENT: return Vec3 tuple (NON Vector3)
// per coerenza con Vec3 api/types. I 4 call site originali leggono .x/.y/.z
// scalari → cambieranno a [0]/[1]/[2] (verbose ma engine-neutral).
//
// raycastHit (engine-neutral, PR2) ritorna {point, distance}; raycast legacy
// ritorna Vector3. Wrapping/extract uniformato a Vec3.

export function raycastDispatch(
  adapter: ViewerAdapter,
  screenX: number,
  screenY: number,
): Vec3 | null {
  if (canUseEngineNeutralCamera(adapter)) {
    const hit = adapter.raycastHit(screenX, screenY);
    return hit !== null ? hit.point : null;
  }
  if (!canRaycast(adapter)) return null;
  const hit: THREE.Vector3 | null = adapter.raycast(screenX, screenY);
  return hit !== null ? [hit.x, hit.y, hit.z] : null;
}

// PR4 — resize helper. Codex INDIPENDENT DPR strategy A: usa
// adapter.getViewport().pixelRatio corrente (capped at init), NON
// window.devicePixelRatio raw. Preserva il fill-rate governor (1.25 mobile /
// 2.0 desktop) settato all'init dell'adapter. NO recovery dinamico su drag
// tra display con DPR diverso (PR future).

export interface BridgeResizeInput {
  readonly width: number;
  readonly height: number;
}

export function resizeDispatch(adapter: ViewerAdapter, size: BridgeResizeInput): void {
  if (canUseEngineNeutralCamera(adapter)) {
    const pixelRatio = adapter.getViewport().pixelRatio;
    adapter.resize({ width: size.width, height: size.height, pixelRatio });
    return;
  }
  // Legacy fallback: ricostruisce il loop ResizeObserver pre-PR4 (no
  // pixelRatio update, no composer setSize esplicito — fake adapter test
  // non hanno renderer/camera/composer reali).
  const renderer = adapter.getRenderer();
  const camera = adapter.getCamera();
  renderer.setSize(size.width, size.height, false);
  const nextAspect = size.width / Math.max(1, size.height);
  if (camera.aspect !== nextAspect) {
    camera.aspect = nextAspect;
    camera.updateProjectionMatrix();
  }
}
